import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Heart, 
  Target, 
  Users, 
  CheckCircle,
  ArrowRight,
  Mail,
  Award,
  Globe,
  Building2,
  Search,
  Shield
} from "lucide-react";

const CSRSolutions = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Heart,
      title: "CSR Strategy Development",
      description: "Comprehensive CSR strategy formulation aligned with business objectives and social impact goals for sustainable development.",
      features: ["Strategy Formulation", "Impact Assessment", "Stakeholder Mapping", "Sustainability Planning"]
    },
    {
      icon: Target,
      title: "Project Formulation",
      description: "Expert project design and formulation for CSR initiatives that create meaningful social impact and measurable outcomes.",
      features: ["Project Design", "Impact Framework", "Budget Planning", "Timeline Development"]
    },
    {
      icon: Users,
      title: "Implementation Support",
      description: "End-to-end implementation support for CSR projects including execution, monitoring, and stakeholder engagement.",
      features: ["Project Execution", "Stakeholder Engagement", "Community Outreach", "Partnership Development"]
    },
    {
      icon: Award,
      title: "Impact Assessment",
      description: "Comprehensive impact measurement and evaluation to demonstrate CSR value and ensure continuous improvement.",
      features: ["Impact Measurement", "Evaluation Studies", "Reporting", "Best Practice Documentation"]
    },
    {
      icon: Globe,
      title: "Multi-Sector Expertise",
      description: "Specialized CSR solutions across various sectors including education, healthcare, environment, and community development.",
      features: ["Education Programs", "Healthcare Initiatives", "Environmental Projects", "Community Development"]
    },
    {
      icon: Shield,
      title: "Compliance & Reporting",
      description: "CSR compliance management and comprehensive reporting to meet regulatory requirements and stakeholder expectations.",
      features: ["Regulatory Compliance", "Annual Reporting", "Documentation", "Audit Support"]
    }
  ];

  const stats = [
    { number: "100+", label: "CSR Projects", icon: Heart },
    { number: "50+", label: "Companies Served", icon: Building2 },
    { number: "1M+", label: "Lives Impacted", icon: Users },
    { number: "8+", label: "Years Experience", icon: Award }
  ];

  const process = [
    {
      step: "01",
      title: "CSR Assessment",
      description: "Comprehensive assessment of current CSR practices, stakeholder needs, and identification of impact opportunities.",
      icon: Search
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Development of customized CSR strategies and detailed project plans aligned with business and social objectives.",
      icon: Target
    },
    {
      step: "03",
      title: "Implementation",
      description: "Expert execution of CSR initiatives with stakeholder engagement, community outreach, and impact monitoring.",
      icon: Users
    },
    {
      step: "04",
      title: "Evaluation & Reporting",
      description: "Comprehensive impact assessment, evaluation, and reporting to demonstrate value and ensure continuous improvement.",
      icon: Award
    }
  ];

  const benefits = [
    "Comprehensive CSR expertise",
    "Multi-sector project experience",
    "Proven impact measurement",
    "Stakeholder engagement excellence",
    "Regulatory compliance support",
    "Sustainable development focus"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-rose-900 via-pink-800 to-purple-900 text-white pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Geometric Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-24 h-24 bg-white transform rotate-45"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-white transform rotate-45"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-white transform rotate-45"></div>
          <div className="absolute bottom-20 right-1/3 w-12 h-12 bg-white transform rotate-45"></div>
        </div>
        
        <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold border border-white/30">
                  <Heart className="h-5 w-5 mr-3" />
                  Corporate Social Responsibility
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Meaningful <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-purple-300">CSR Solutions</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  Expertise in project formulation, implementation, and assessment to support corporate social responsibility initiatives across sectors.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                 onClick={()=>navigate('/contactus')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-pink-400 to-purple-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                  Start Your CSR Journey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
                <motion.button
                 onClick={()=>navigate('/projects')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-rose-900 transition-all duration-300"
                >
                  View Our Impact
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Our CSR Impact</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                        className="text-center"
                      >
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <stat.icon className="h-8 w-8 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-white">{stat.number}</div>
                        <div className="text-white/80 text-sm">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Proven <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-pink-600">CSR Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach that ensures effective CSR strategy development and meaningful social impact.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-200 hover:border-rose-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-2xl flex items-center justify-center">
                        <step.icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-4xl font-bold text-gray-200">{step.step}</div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-rose-300 to-pink-300 transform -translate-y-1/2"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-pink-600">CSR Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From strategy development to impact assessment, we provide end-to-end CSR solutions that create meaningful social impact.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 h-full border border-gray-200 hover:border-rose-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-rose-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-rose-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-pink-600">CSR Solutions?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Partner with us for comprehensive CSR solutions that create meaningful social impact and drive sustainable development.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-rose-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-rose-600 to-pink-600 rounded-3xl p-8 text-white"
            >
              <div className="space-y-6">
                <div className="text-center">
                  <Heart className="h-16 w-16 mx-auto mb-4 text-white/80" />
                  <h3 className="text-2xl font-bold mb-2">Social Impact Excellence</h3>
                  <p className="text-white/90">Your impact, our commitment</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <h4 className="text-lg font-semibold mb-4">Start Your CSR Journey</h4>
                  <p className="text-white/90 mb-6">
                    Ready to create meaningful social impact? Let's discuss how our CSR solutions can help you make a positive difference in communities.
                  </p>
                  <motion.button
                    onClick={() => navigate('/contactus')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-white text-rose-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center"
                  >
                    Contact Us Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-rose-600 to-pink-600">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Create <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">Meaningful Impact</span>?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-white/90">
              Let's discuss your CSR needs and create comprehensive solutions that drive positive social change and sustainable development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              
              <motion.button
               onClick={()=>navigate('/contactus')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-rose-600 transition-all duration-300 flex items-center justify-center"
              >
                <Mail className="mr-2 h-5 w-5" />
                Discuss Your Needs
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CSRSolutions;